<template>
  <div class="modal-overlay" @mousedown.self="close">
    <section
      ref="dialog"
      class="request-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manual-request-title"
      aria-describedby="manual-request-description"
      @keydown="handleDialogKeydown"
    >
      <header>
        <div>
          <p class="eyebrow">Inclusão temporária</p>
          <h2 id="manual-request-title">Adicionar solicitação</h2>
          <p id="manual-request-description">Cadastre o pedido manualmente até a ativação dos gatilhos do Atividades.</p>
        </div>
        <button class="close-button" type="button" :disabled="submitting" aria-label="Fechar formulário" @click="close">
          <fa :icon="['far', 'xmark']" aria-hidden="true" />
        </button>
      </header>

      <form @submit.prevent="submit">
        <section v-if="error" class="form-error" role="alert">
          <fa :icon="['fas', 'triangle-exclamation']" aria-hidden="true" />
          <div><strong>Não foi possível adicionar a solicitação.</strong><p>{{ error }}</p></div>
        </section>

        <div class="field wide enrollment-field">
          <label for="manual-enrollment-search">Aluno ou matrícula *</label>
          <div
            class="combobox"
            role="combobox"
            aria-haspopup="listbox"
            :aria-expanded="optionsOpen"
            aria-owns="manual-enrollment-options"
          >
            <fa :icon="['fas', 'magnifying-glass']" aria-hidden="true" />
            <input
              id="manual-enrollment-search"
              ref="searchInput"
              v-model.trim="enrollmentSearch"
              type="search"
              autocomplete="off"
              placeholder="Digite nome, RM ou código da matrícula"
              :aria-controls="optionsOpen ? 'manual-enrollment-options' : undefined"
              :aria-activedescendant="activeOptionId"
              :aria-describedby="searchHelpId"
              @input="scheduleEnrollmentSearch"
              @keydown="handleSearchKeydown"
              @focus="optionsOpen = enrollmentOptions.length > 0"
            >
            <span v-if="enrollmentLoading" class="loading-label" role="status">Buscando...</span>
          </div>
          <small :id="searchHelpId">Informe pelo menos 3 caracteres e selecione uma matrícula do período atual.</small>

          <ul v-if="optionsOpen" id="manual-enrollment-options" class="enrollment-options" role="listbox">
            <li
              v-for="(enrollment, index) in enrollmentOptions"
              :id="optionId(index)"
              :key="enrollment.id"
              role="option"
              :aria-selected="highlightedIndex === index"
              :class="{ highlighted: highlightedIndex === index }"
              @mousedown.prevent="selectEnrollment(enrollment)"
              @mousemove="highlightedIndex = index"
            >
              <strong>{{ enrollment.student_name }}</strong>
              <span>RM {{ enrollment.student_rm }} · {{ enrollment.unit }} · {{ enrollment.series }} / {{ enrollment.class }}</span>
              <small>Matrícula {{ enrollment.code || enrollment.id }} · Período letivo {{ enrollment.period }}</small>
            </li>
          </ul>
          <p v-else-if="searchPerformed && !enrollmentLoading && !selectedEnrollment" class="empty-search" role="status">Nenhuma matrícula elegível encontrada.</p>

          <div v-if="selectedEnrollment" class="selected-enrollment" role="status">
            <div><strong>{{ selectedEnrollment.student_name }}</strong><span>RM {{ selectedEnrollment.student_rm }} · {{ selectedEnrollment.unit }} · Período {{ selectedEnrollment.period }}</span></div>
            <button type="button" :disabled="submitting" @click="clearEnrollment">Alterar</button>
          </div>
        </div>

        <div class="form-grid">
          <label class="field wide">
            <span>Período letivo *</span>
            <input :value="selectedEnrollment?.period || ''" type="text" readonly aria-readonly="true" placeholder="Selecione uma matrícula">
            <small>Definido pela matrícula selecionada.</small>
          </label>
          <label class="field wide">
            <span>Incidência *</span>
            <select v-model="form.incidence" required><option disabled value="">Selecione</option><option v-for="option in rule.incidences || []" :key="option.value" :value="option.value">{{ option.label }}</option></select>
          </label>
          <label class="field wide">
            <span>Motivo *</span>
            <select v-model="form.motive" required><option disabled value="">Selecione</option><option v-for="option in rule.motives || []" :key="option.value" :value="option.value">{{ option.label }}</option></select>
          </label>
          <label class="field wide">
            <span>Descrição do motivo *</span>
            <textarea v-model.trim="form.motive_description" rows="4" maxlength="5000" required placeholder="Descreva o contexto informado pela família."></textarea>
            <small>{{ form.motive_description.length }}/5000 caracteres</small>
          </label>
          <label class="field wide">
            <span>Comprovante{{ requiresSupportingDocument ? ' *' : '' }}</span>
            <input
              ref="supportingDocumentInput"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              :required="requiresSupportingDocument"
              :aria-required="requiresSupportingDocument"
              aria-describedby="supporting-document-help supporting-document-error"
              @change="selectSupportingDocument"
            >
            <small id="supporting-document-help">{{ supportingDocumentHelp }}</small>
            <small v-if="attachmentError" id="supporting-document-error" class="field-error" role="alert">{{ attachmentError }}</small>
          </label>
        </div>

        <footer>
          <button class="button secondary" type="button" :disabled="submitting" @click="close">Cancelar</button>
          <button class="button primary" type="submit" :disabled="!canSubmit || submitting">
            <span v-if="submitting" class="spinner" aria-hidden="true"></span>
            {{ submitting ? 'Adicionando...' : 'Adicionar solicitação' }}
          </button>
        </footer>
      </form>
    </section>
  </div>
</template>

<script>
import DiscountDecisionService from './discountDecision.service'

export default {
  name: 'DiscountDecisionRequestModal',
  props: {
    rule: { type: Object, required: true }
  },
  emits: ['close', 'created'],
  data () {
    return {
      previousFocus: null,
      previousBodyOverflow: '',
      enrollmentSearch: '',
      enrollmentOptions: [],
      selectedEnrollment: null,
      enrollmentLoading: false,
      enrollmentTimer: null,
      enrollmentController: null,
      searchPerformed: false,
      optionsOpen: false,
      highlightedIndex: -1,
      submitting: false,
      error: null,
      supportingDocument: null,
      attachmentError: null,
      idempotencyKey: this.createIdempotencyKey(),
      form: { incidence: '', motive: '', motive_description: '' }
    }
  },
  computed: {
    canSubmit () {
      return this.selectedEnrollment && this.form.incidence && this.form.motive && this.form.motive_description && (!this.requiresSupportingDocument || this.supportingDocument) && !this.attachmentError
    },
    requiresSupportingDocument () {
      return ['partial_income_loss', 'responsible_unemployment'].includes(this.form.motive)
    },
    supportingDocumentHelp () {
      return this.requiresSupportingDocument
        ? 'Obrigatório para perda de renda ou desemprego. Envie PDF, imagem ou documento de até 10 MB.'
        : 'PDF, imagem ou documento de até 10 MB.'
    },
    activeOptionId () { return this.highlightedIndex >= 0 ? this.optionId(this.highlightedIndex) : undefined },
    searchHelpId () { return 'manual-enrollment-search-help' }
  },
  mounted () {
    this.previousFocus = document.activeElement
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    this.$nextTick(() => this.$refs.searchInput?.focus())
  },
  beforeUnmount () {
    window.clearTimeout(this.enrollmentTimer)
    this.enrollmentController?.abort()
    document.body.style.overflow = this.previousBodyOverflow
    this.previousFocus?.focus?.()
  },
  methods: {
    createIdempotencyKey () {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID()
      return `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`
    },
    scheduleEnrollmentSearch () {
      this.selectedEnrollment = null
      this.optionsOpen = false
      this.searchPerformed = false
      window.clearTimeout(this.enrollmentTimer)
      this.enrollmentController?.abort()
      if (this.enrollmentSearch.length < 3) {
        this.enrollmentOptions = []
        return
      }
      this.enrollmentTimer = window.setTimeout(this.searchEnrollments, 300)
    },
    async searchEnrollments () {
      this.enrollmentLoading = true
      this.error = null
      const controller = new AbortController()
      this.enrollmentController = controller
      try {
        const response = await DiscountDecisionService.searchEnrollments(this.enrollmentSearch, controller.signal)
        this.enrollmentOptions = response.data.data || []
        this.highlightedIndex = this.enrollmentOptions.length ? 0 : -1
        this.optionsOpen = this.enrollmentOptions.length > 0
        this.searchPerformed = true
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') this.error = error.response?.data?.message || 'Não foi possível buscar as matrículas.'
      } finally {
        if (this.enrollmentController === controller) this.enrollmentLoading = false
      }
    },
    selectEnrollment (enrollment) {
      this.selectedEnrollment = enrollment
      this.enrollmentSearch = enrollment.student_name
      this.enrollmentOptions = []
      this.optionsOpen = false
      this.highlightedIndex = -1
    },
    clearEnrollment () {
      this.selectedEnrollment = null
      this.enrollmentSearch = ''
      this.searchPerformed = false
      this.$nextTick(() => this.$refs.searchInput?.focus())
    },
    handleSearchKeydown (event) {
      if (!this.optionsOpen || !this.enrollmentOptions.length) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.highlightedIndex = (this.highlightedIndex + 1) % this.enrollmentOptions.length
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.highlightedIndex = (this.highlightedIndex - 1 + this.enrollmentOptions.length) % this.enrollmentOptions.length
      } else if (event.key === 'Enter' && this.highlightedIndex >= 0) {
        event.preventDefault()
        this.selectEnrollment(this.enrollmentOptions[this.highlightedIndex])
      } else if (event.key === 'Escape') {
        event.preventDefault()
        this.optionsOpen = false
      }
    },
    handleDialogKeydown (event) {
      if (event.key === 'Escape' && !this.optionsOpen && !this.submitting) {
        event.preventDefault()
        this.close()
      }
      if (event.key !== 'Tab') return
      const focusable = [...this.$refs.dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')]
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    optionId (index) { return `manual-enrollment-option-${index}` },
    selectSupportingDocument (event) {
      const file = event.target.files?.[0] || null
      this.supportingDocument = null
      this.attachmentError = null
      if (!file) return
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx']
      const extension = file.name.split('.').pop()?.toLowerCase()
      if (!allowedExtensions.includes(extension)) {
        this.attachmentError = 'Formato não permitido. Use PDF, JPG, PNG, DOC ou DOCX.'
      } else if (file.size > 10 * 1024 * 1024) {
        this.attachmentError = 'O comprovante deve ter no máximo 10 MB.'
      } else {
        this.supportingDocument = file
      }
      if (this.attachmentError) event.target.value = ''
    },
    async submit () {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.error = null
      try {
        const response = await DiscountDecisionService.createRequest({
          enrollment_id: this.selectedEnrollment.id,
          incidence: this.form.incidence,
          motive: this.form.motive,
          motive_description: this.form.motive_description,
          supporting_document: this.supportingDocument
        }, this.idempotencyKey)
        this.$emit('created', response.data)
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Erro inesperado.'
      } finally {
        this.submitting = false
      }
    },
    close () {
      if (!this.submitting) this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
$secondary-text: darken($color-text, 20%);
.modal-overlay { align-items: center; background: rgba($color-dev, .55); display: flex; inset: 0; justify-content: center; padding: $hmg_mid; position: fixed; z-index: 9500; }
.request-dialog { background: $color-secondary; border: 1px solid $color-component; border-radius: 24px; box-shadow: 0 18px 60px rgba($color-dev, .24); max-height: calc(100vh - 40px); max-width: 720px; overflow: auto; width: 100%; > header { align-items: flex-start; border-bottom: 1px solid $color-component; display: flex; gap: $hmg_mid; justify-content: space-between; padding: $hmg_mid $mg; h2 { font-size: 20px; margin: 0 0 $hmg_mini; } p { color: $secondary-text; margin: 0; } } }
.eyebrow { color: $color-primary !important; font-size: $font-size-small; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.close-button { align-items: center; background: transparent; border: 0; border-radius: 10px; color: $color-dev; cursor: pointer; display: inline-flex; font-size: 20px; justify-content: center; min-height: 44px; min-width: 44px; &:focus-visible { outline: 3px solid rgba($color-primary, .25); } }
form { padding: $mg; }
.form-error { align-items: flex-start; background: rgba($color-error, .07); border: 1px solid rgba($color-error, .3); border-radius: 14px; color: $color-error-2; display: flex; gap: $hmg_small; margin-bottom: $hmg_mid; padding: $hmg_small $hmg_mid; p { margin: 2px 0 0; } }
.field { display: grid; gap: $hmg_mini; min-width: 0; > span, > label { font-weight: 700; } > small { color: $secondary-text; } input, select, textarea { background: $color-secondary; border: 1px solid $color-mid-grey; border-radius: 14px; color: $color-dev; font: inherit; min-height: 46px; padding: $hmg_small; width: 100%; &:focus { border-color: $color-primary; box-shadow: 0 0 0 3px rgba($color-primary, .12); outline: 0; } } input[readonly] { background: $color-grey; color: $secondary-text; } input[type="file"] { cursor: pointer; } textarea { resize: vertical; } .field-error { color: $color-error-2; font-weight: 600; } }
.enrollment-field { position: relative; > label { display: block; font-weight: 700; margin-bottom: $hmg_mini; } > small { color: $secondary-text; display: block; margin-top: $hmg_mini; } }
.combobox { align-items: center; border: 1px solid $color-mid-grey; border-radius: 14px; display: flex; min-height: 46px; padding: 0 $hmg_small; svg { color: $color-primary; } input { background: transparent; border: 0; flex: 1; font: inherit; min-width: 0; outline: 0; padding: 0 $hmg_small; } &:focus-within { border-color: $color-primary; box-shadow: 0 0 0 3px rgba($color-primary, .12); } }
.loading-label { color: $secondary-text; font-size: $font-size-small; }
.enrollment-options { background: $color-secondary; border: 1px solid $color-component; border-radius: 14px; box-shadow: 0 12px 30px rgba($color-dev, .14); left: 0; list-style: none; margin: $hmg_mini 0 0; max-height: 280px; overflow-y: auto; padding: $hmg_mini; position: absolute; right: 0; z-index: 2; li { border-radius: 10px; cursor: pointer; display: grid; gap: 2px; padding: $hmg_small; &.highlighted { background: rgba($color-primary, .08); } span, small { color: $secondary-text; } } }
.empty-search { color: $secondary-text; margin: $hmg_mini 0 0; }
.selected-enrollment { align-items: center; background: $color-grey; border: 1px solid $color-component; border-radius: 14px; display: flex; gap: $hmg_small; justify-content: space-between; margin-top: $hmg_small; padding: $hmg_small; div { display: grid; gap: 2px; } span { color: $secondary-text; } button { background: transparent; border: 0; color: $color-primary; cursor: pointer; font-weight: 700; min-height: 44px; padding: 0 $hmg_small; } }
.form-grid { display: grid; gap: $hmg_mid; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: $hmg_mid; }.wide { grid-column: 1 / -1; }
footer { align-items: center; border-top: 1px solid $color-component; display: flex; gap: $hmg_small; justify-content: flex-end; margin-top: $mg; padding-top: $hmg_mid; }
.button { align-items: center; border: 1px solid transparent; border-radius: 14px; cursor: pointer; display: inline-flex; font: inherit; font-weight: 700; justify-content: center; min-height: 46px; padding: 0 $hmg_mid; &.primary { background: $color-primary; color: $color-secondary; } &.secondary { background: $color-secondary; border-color: $color-mid-grey; color: $color-dev; } &:disabled { cursor: not-allowed; opacity: .55; } &:focus-visible { outline: 3px solid rgba($color-primary, .25); outline-offset: 2px; } }
.spinner { animation: spin .8s linear infinite; border: 2px solid rgba($color-secondary, .45); border-radius: 50%; border-top-color: $color-secondary; height: 16px; margin-right: $hmg_mini; width: 16px; }@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 720px) { .modal-overlay { align-items: flex-end; padding: 0; }.request-dialog { border-bottom-left-radius: 0; border-bottom-right-radius: 0; max-height: 94vh; }.request-dialog > header, form { padding: $hmg_mid; }.form-grid { grid-template-columns: 1fr; }.wide { grid-column: auto; } footer { align-items: stretch; flex-direction: column-reverse; }.button { width: 100%; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }
</style>
